/* eslint-disable consistent-return */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useEffect, useRef, cloneElement } from 'react';
import { bool, node } from 'prop-types';
import useForkRef from '../../hooks/useForkRef';
import { ownerDocument } from '../../utils/headerUtils';
import { candidatesSelector } from '../../constants';

const defaultGetTabbable = (root) => root.querySelectorAll(candidatesSelector);

/**
 * The FocusBoundary component traps focus and accessability to a specific DOM node
 */
const FocusBoundary = ({ children = null, open = false }) => {

  const ignoreNextEnforceFocus = useRef(false);
  const sentinelStart = useRef(null);
  const sentinelEnd = useRef(null);
  const nodeToRestore = useRef(null);
  const reactFocusEventTarget = useRef(null);
  const activated = useRef(false);

  const rootRef = useRef(null);
  const handleRef = useForkRef(children.ref, rootRef);
  const lastKeydown = useRef(null);

  useEffect(() => {
    if (!open || !rootRef.current) return;

    activated.current = true;

    const doc = ownerDocument(rootRef.current);

    if (!rootRef.current.contains(doc.activeElement)) {
      ignoreNextEnforceFocus.current = true;
      rootRef.current.focus();
    }

    const contain = (nativeEvent) => {
      const rootElement = rootRef.current;
      if (!open || !rootElement) return;

      if (!doc.hasFocus() || ignoreNextEnforceFocus.current) {
        ignoreNextEnforceFocus.current = false;
        return;
      }

      if (doc.activeElement?.tagName?.toLowerCase() === 'input') return;
      if (!rootElement.contains(doc.activeElement)) return;

      if ((nativeEvent && reactFocusEventTarget.current !== nativeEvent.target)
        || doc.activeElement !== reactFocusEventTarget.current) {
        reactFocusEventTarget.current = null;
      }
      if (reactFocusEventTarget.current !== null) return;

      if (!activated.current) return;

      let tabbable = [];
      if (doc.activeElement === sentinelStart.current || doc.activeElement === sentinelEnd.current) {
        tabbable = defaultGetTabbable(rootRef.current);
      }

      if (tabbable.length <= 0) {
        if (doc.activeElement !== rootElement && !ignoreNextEnforceFocus.current) {
          ignoreNextEnforceFocus.current = true;
          rootElement.focus();
        }
        return;
      }

      const isShiftTab = Boolean(lastKeydown.current?.shiftKey && lastKeydown.current?.key === 'Tab');
      const focusNext = tabbable[0];
      const focusPrevious = tabbable[tabbable.length - 1];

      ignoreNextEnforceFocus.current = true;
      if (isShiftTab) {
        focusPrevious.focus();
      } else {
        focusNext.focus();
      }
    };

    const containRef = { current: contain };

    const handleFocusIn = (event) => {
      if (!open) return;
      if (document.activeElement && !rootRef.current.contains(document.activeElement)) return;
      if (containRef.current) {
        containRef.current();
      }
    };

    const loopFocus = (nativeEvent) => {
      lastKeydown.current = nativeEvent;

      if (nativeEvent.key !== 'Tab') return;

      if (doc.activeElement === rootRef.current && nativeEvent.shiftKey) {
        ignoreNextEnforceFocus.current = true;
        sentinelEnd.current.focus();
      }
    };

    doc.addEventListener('focusin', handleFocusIn);
    doc.addEventListener('keydown', loopFocus, true);

    const interval = setInterval(() => {
      if (doc.activeElement.tagName === 'BODY') {
        if (containRef.current) {
          containRef.current();
        }
      }
    }, 50);

    return () => {
      clearInterval(interval);
      doc.removeEventListener('focusin', handleFocusIn);
      doc.removeEventListener('keydown', loopFocus, true);

      if (nodeToRestore.current?.focus) {
        ignoreNextEnforceFocus.current = true;
        nodeToRestore.current.focus();
      }
      nodeToRestore.current = null;
    };
  }, [open]);

  const onFocus = (event) => {
    if (nodeToRestore.current === null) {
      nodeToRestore.current = event.relatedTarget;
    }
    activated.current = true;
    reactFocusEventTarget.current = event.target;
  };

  const handleFocusSentinel = (event) => {
    if (nodeToRestore.current === null) {
      nodeToRestore.current = event.relatedTarget;
    }
    activated.current = true;
  };

  return (
    <>
      <div
        tabIndex={open ? 0 : -1}
        onFocus={handleFocusSentinel}
        ref={sentinelStart}
        data-testid="sentinelStart"
      />
      {cloneElement(children, { ref: handleRef, onFocus })}
      <div
        tabIndex={open ? 0 : -1}
        onFocus={handleFocusSentinel}
        ref={sentinelEnd}
        data-testid="sentinelEnd"
      />
    </>
  );
};

FocusBoundary.displayName = 'FocusBoundary';

FocusBoundary.propTypes = {
  children: node,
  open: bool,
};

export { FocusBoundary };
